import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Navbar from '../components/Navbar'
import { useAuth } from '../hooks/useAuth'
import { usePractice } from '../hooks/usePractice'
import { getClients, addClient, deleteClient, archiveClient, unarchiveClient, getClientOutstanding, countClientOpenRequests } from '../lib/supabase'
import { useToast } from '../components/Toast'
import usePageTitle from '../hooks/usePageTitle'

export default function Dashboard() {
  const { user } = useAuth()
  const { practiceId, role, loading: practiceLoading } = usePractice()
  const toast = useToast()
  usePageTitle('Dashboard')
  const [clients, setClients]       = useState([])
  const [outstanding, setOutstanding] = useState({})
  const [loading, setLoading]       = useState(true)
  const [showModal, setShowModal]   = useState(false)
  const [form, setForm]             = useState({ name: '', email: '', internalReference: '', contactName: '' })
  const [saving, setSaving]         = useState(false)
  const [search, setSearch]         = useState('')
  const [showArchived, setShowArchived] = useState(false)

  // ─── Archive confirmation flow ──────────────────────────────────────────
  // When a client has open requests, archiving prompts for whether to also
  // cancel those requests or leave them. Stored as an object so the prompt
  // has full context: { client, openCount } -- null when no prompt is open.
  const [archivePrompt, setArchivePrompt] = useState(null)
  const [archiveBusy, setArchiveBusy]     = useState(false)

  const loadData = async () => {
    if (!practiceId) return
    const [{ data: clientData, error }, { data: outData }] = await Promise.all([
      getClients(practiceId),
      getClientOutstanding(practiceId),
    ])
    if (error) toast(error.message, 'error')
    else setClients(clientData || [])

    const map = {}
    for (const row of (outData || [])) {
      map[row.client_id] = row.outstanding_count || 0
    }
    setOutstanding(map)
    setLoading(false)
  }

  useEffect(() => {
    if (!practiceLoading && practiceId) loadData()
  }, [practiceId, practiceLoading])

  const handleCreate = async (e) => {
    e.preventDefault()
    setSaving(true)
    const { error } = await addClient(practiceId, form.name.trim(), form.email.trim(), {
      // Pass optional extras only if filled in. addClient will turn empty
      // strings into NULL on its side.
      internalReference: form.internalReference,
      contactName:       form.contactName,
    })
    setSaving(false)
    if (error) { toast(error.message, 'error'); return }
    toast(`Client "${form.name}" added!`, 'success')
    setForm({ name: '', email: '', internalReference: '', contactName: '' })
    setShowModal(false)
    loadData()
  }

  const handleDelete = async (client) => {
    if (!window.confirm(`Permanently remove ${client.name}? This will delete all their requests and files.`)) return
    const { error } = await deleteClient(client.id)
    if (error) { toast(error.message, 'error'); return }
    toast(`${client.name} removed`, 'default')
    loadData()
  }

  // Archive flow has two paths:
  //   1. If the client has no open requests, archive immediately
  //      (same as the old behaviour, no extra dialog).
  //   2. If they have open requests, surface the count and ask the
  //      bookkeeper to choose whether to also cancel them.
  //
  // This avoids the silent inconsistent state where a client is archived
  // but their open requests still sit there, and avoids forcing the user
  // to clean up first when they don't want to.
  const handleArchive = async (client) => {
    const { data: openCount, error } = await countClientOpenRequests(client.id)
    if (error) { toast(error.message, 'error'); return }
    if (openCount === 0) {
      // No open requests, just archive directly
      await commitArchive(client, false)
      return
    }
    // Open requests exist: pop the prompt
    setArchivePrompt({ client, openCount })
  }

  // Performs the archive (and optionally the cascade-cancel of open
  // requests). Called from both the no-prompt path and the prompt's
  // commit buttons.
  const commitArchive = async (client, cancelOpenRequests) => {
    setArchiveBusy(true)
    const { error } = await archiveClient(client.id, { cancelOpenRequests })
    setArchiveBusy(false)
    setArchivePrompt(null)
    if (error) { toast(error.message, 'error'); return }
    if (cancelOpenRequests) {
      toast(`${client.name} archived. Open requests were cancelled.`, 'success')
    } else {
      toast(`${client.name} archived. They won't count toward your billing.`, 'success')
    }
    loadData()
  }

  const handleUnarchive = async (client) => {
    const { error } = await unarchiveClient(client.id)
    if (error) { toast(error.message, 'error'); return }
    toast(`${client.name} restored to active clients`, 'success')
    loadData()
  }

  const activeClients   = clients.filter(c => !c.archived)
  const archivedClients = clients.filter(c =>  c.archived)

  // Match search query against name, email, internal_reference, and
  // contact_name. Empty fields don't match anything; lowercase comparison
  // throughout for case-insensitive search.
  const matchesSearch = (c) => {
    const q = search.toLowerCase()
    if (!q) return true
    return (
      c.name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.internal_reference?.toLowerCase().includes(q) ||
      c.contact_name?.toLowerCase().includes(q)
    )
  }
  const filteredActive   = activeClients.filter(matchesSearch)
  const filteredArchived = archivedClients.filter(matchesSearch)

  const totalOutstanding = Object.values(outstanding).reduce((a, b) => a + b, 0)

  const ClientCard = ({ client, archived = false }) => {
    const pendingCount = outstanding[client.id] || 0
    return (
      <div className="card" style={{
        padding: '18px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 16, flexWrap: 'wrap',
        opacity: archived ? 0.75 : 1,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: archived ? 'var(--surface-2)' : 'var(--accent-light)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'var(--font-display)', fontSize: '1rem',
            color: archived ? 'var(--ink-muted)' : 'var(--accent)', flexShrink: 0,
          }}>
            {client.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 500, fontSize: '0.95rem' }}>{client.name}</span>
              {archived && (
                <span style={{ background: 'var(--surface-2)', color: 'var(--ink-muted)', fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 100 }}>
                  Archived
                </span>
              )}
              {!archived && pendingCount > 0 && (
                <span style={{ background: 'var(--amber-light)', color: 'var(--amber)', fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 100 }}>
                  {pendingCount} outstanding
                </span>
              )}
              {!archived && pendingCount === 0 && outstanding[client.id] !== undefined && (
                <span style={{ background: 'var(--green-light)', color: 'var(--green)', fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 100 }}>
                  ✓ All done
                </span>
              )}
            </div>
            <div style={{
              fontSize: '0.82rem', color: 'var(--ink-muted)',
              display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            }}>
              {client.internal_reference && (
                <span style={{
                  background: 'var(--surface-2)',
                  color: 'var(--ink-muted)',
                  padding: '1px 7px',
                  borderRadius: 4,
                  fontSize: '0.74rem',
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {client.internal_reference}
                </span>
              )}
              <span>{client.email}</span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to={`/clients/${client.id}`} className="btn btn-secondary btn-sm">
            View requests →
          </Link>
          {!archived && (
            <Link to={`/clients/${client.id}/new-request`} className="btn btn-primary btn-sm">
              + New request
            </Link>
          )}
          {archived ? (
            <button className="btn btn-ghost btn-sm" onClick={() => handleUnarchive(client)} title="Restore client">
              ↩ Restore
            </button>
          ) : (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => handleArchive(client)}
              title="Archive client. Removes from billing count."
              style={{ color: 'var(--ink-muted)' }}
            >
              📦 Archive
            </button>
          )}
          {role !== 'assistant' && (
            <button
              className="btn btn-ghost btn-sm btn-icon"
              onClick={() => handleDelete(client)}
              title="Permanently delete client"
              style={{ color: '#dc2626' }}
            >
              🗑
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      <Navbar />
      <main className="container" style={{ padding: '40px 24px' }}>

        {/* Header */}
        <div className="flex-between fade-up-1" style={{ marginBottom: 32, flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h2 style={{ marginBottom: 4 }}>Your Clients</h2>
            <p style={{ color: 'var(--ink-muted)', fontSize: '0.9rem' }}>
              Manage clients and send receipt requests
            </p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            + Add client
          </button>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 32 }}>
          <div className="card fade-up-1" style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', flexShrink: 0 }}>👥</div>
            <div>
              <div style={{ fontSize: '1.6rem', fontFamily: 'var(--font-display)', lineHeight: 1 }}>{activeClients.length}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--ink-muted)', marginTop: 2 }}>Active clients</div>
            </div>
          </div>
          <div className="card fade-up-2" style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: totalOutstanding > 0 ? 'var(--amber-light)' : 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', flexShrink: 0 }}>
              {totalOutstanding > 0 ? '⏳' : '✅'}
            </div>
            <div>
              <div style={{ fontSize: '1.6rem', fontFamily: 'var(--font-display)', lineHeight: 1, color: totalOutstanding > 0 ? 'var(--amber)' : 'var(--green)' }}>{totalOutstanding}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--ink-muted)', marginTop: 2 }}>Outstanding receipts</div>
            </div>
          </div>
        </div>

        {/* Search */}
        {clients.length > 0 && (
          <div className="fade-up-2" style={{ marginBottom: 20 }}>
            <input
              type="search"
              className="input"
              placeholder="Search by name, email, contact, or reference…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ maxWidth: 340 }}
            />
          </div>
        )}

        {/* Active clients */}
        {loading ? (
          <div className="loading-screen" style={{ minHeight: 300 }}>
            <div className="spinner" />
          </div>
        ) : activeClients.length === 0 && archivedClients.length === 0 ? (
          <div className="card empty-state fade-up-2">
            <div className="icon">👤</div>
            <h3>No clients yet</h3>
            <p>Add your first client to get started sending receipt requests.</p>
            <button className="btn btn-primary" onClick={() => setShowModal(true)}>
              + Add your first client
            </button>
            <p style={{ marginTop: 20, fontSize: '0.88rem', color: 'var(--ink-muted)' }}>
              New to Receipt Chaser?{' '}
              <Link to="/support" style={{ color: 'var(--accent)', fontWeight: 500 }}>
                Read the getting-started guide
              </Link>.
            </p>
          </div>
        ) : (
          <>
            <div className="fade-up-3" style={{ display: 'grid', gap: 12 }}>
              {filteredActive.map(client => (
                <ClientCard key={client.id} client={client} />
              ))}
            </div>

            {/* Archived section */}
            {archivedClients.length > 0 && (
              <div style={{ marginTop: 32 }}>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setShowArchived(v => !v)}
                  style={{ marginBottom: 14, color: 'var(--ink-muted)', display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  <span style={{ fontSize: '0.75rem', transition: 'transform 0.15s', display: 'inline-block', transform: showArchived ? 'rotate(90deg)' : 'none' }}>▶</span>
                  Archived clients ({archivedClients.length})
                </button>
                {showArchived && (
                  <div style={{ display: 'grid', gap: 10 }}>
                    <p style={{ fontSize: '0.82rem', color: 'var(--ink-faint)', marginBottom: 4 }}>
                      Archived clients are not counted toward your billing. You can still view their requests.
                    </p>
                    {filteredArchived.map(client => (
                      <ClientCard key={client.id} client={client} archived />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>

      {/* Add Client Modal */}
      {showModal && (
        <div className="modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add new client</h3>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="cname">Client / company name</label>
                  <input
                    id="cname"
                    className="input"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Acme Ltd"
                    required
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="cemail">Email address</label>
                  <input
                    id="cemail"
                    type="email"
                    className="input"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="client@company.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="ccontact">
                    Contact name <span style={{ color: 'var(--ink-faint)', fontWeight: 400, textTransform: 'none', fontSize: '0.8rem' }}>(optional)</span>
                  </label>
                  <input
                    id="ccontact"
                    className="input"
                    value={form.contactName}
                    onChange={e => setForm(f => ({ ...f, contactName: e.target.value }))}
                    placeholder="Sarah Patel"
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label htmlFor="cref">
                    Internal reference <span style={{ color: 'var(--ink-faint)', fontWeight: 400, textTransform: 'none', fontSize: '0.8rem' }}>(optional)</span>
                  </label>
                  <input
                    id="cref"
                    className="input"
                    value={form.internalReference}
                    onChange={e => setForm(f => ({ ...f, internalReference: e.target.value }))}
                    placeholder="e.g. ACME-001"
                    maxLength={50}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Adding…' : 'Add client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Archive confirmation modal. Only shown when archiving a client
          that has open requests; the no-open-requests path skips this and
          archives directly. Three choices laid out clearly so the user
          doesn't have to guess what each button does. */}
      {archivePrompt && (
        <div className="modal-backdrop" onClick={() => !archiveBusy && setArchivePrompt(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Archive {archivePrompt.client.name}?</h3>
              <button
                className="btn btn-ghost btn-icon"
                onClick={() => setArchivePrompt(null)}
                disabled={archiveBusy}
              >✕</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 12, lineHeight: 1.55 }}>
                This client has{' '}
                <strong>{archivePrompt.openCount} open request{archivePrompt.openCount === 1 ? '' : 's'}</strong>
                {' '}with receipts still outstanding.
              </p>
              <p style={{ marginBottom: 0, color: 'var(--ink-muted)', fontSize: '0.88rem', lineHeight: 1.55 }}>
                Archived clients don't count toward your billing and won't
                receive automatic reminders. You can either cancel the open
                requests too (uploaded receipts stay on record), or archive
                the client but leave the requests intact for now.
              </p>
            </div>
            <div className="modal-footer" style={{ flexWrap: 'wrap', gap: 8 }}>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setArchivePrompt(null)}
                disabled={archiveBusy}
              >
                Don't archive
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => commitArchive(archivePrompt.client, false)}
                disabled={archiveBusy}
              >
                Archive only
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => commitArchive(archivePrompt.client, true)}
                disabled={archiveBusy}
              >
                {archiveBusy
                  ? 'Working…'
                  : `Cancel ${archivePrompt.openCount} request${archivePrompt.openCount === 1 ? '' : 's'} and archive`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
